import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { DocumentStore } from '../documentStore';
import { Extension, ExtensionsInternalService, EXTENSION_STATUS } from '@roc/feature-loan-details';
import { LoanStore } from '@roc/feature-loans';
import { format } from 'date-fns';

export class ExtensionDetailsStore {
  loanStore: LoanStore;
  globalStore: GlobalStore;
  documentStore: DocumentStore;
  private extensionsService: ExtensionsInternalService;
  notes = '';
  requestDate = '';
  isLoading = false;
  totalLoanAmount = '';
  extensionDuration = '';
  totalExtensionFee = '';
  existingMaturityDate = '';
  nextDueDate = '';
  currentExtension: Extension = null;
  customNewMaturityDate: Date | null = null;
  disableNewMaturityDate = true;

  constructor(globalStore: GlobalStore, documentStore: DocumentStore, loanStore: LoanStore) {
    this.globalStore = globalStore;
    this.documentStore = documentStore;
    this.loanStore = loanStore;
    this.extensionsService = new ExtensionsInternalService();

    makeObservable(this, {
      reset: action,
      setField: action,
      isValid: computed,
      isReadOnly: computed,
      notes: observable,
      loadLoanData: action,
      isLoading: observable,
      requestDate: observable,
      newMaturityDate: computed,
      customNewMaturityDate: observable,
      loadExtensionData: action,
      initExtensionDetails: flow,
      saveExtensionDetails: flow,
      totalLoanAmount: observable,
      currentExtension: observable,
      extensionDuration: observable,
      totalExtensionFee: observable,
      existingMaturityDate: observable,
      nextDueDate: observable,
      disableNewMaturityDate: observable,
    });
  }

  *initExtensionDetails(loanId: number, extensionId: number) {
    this.isLoading = true;
    try {
      yield this.loanStore.fetchLoanDetails(loanId.toString());

      const extensionResponse = yield this.extensionsService.getLoanExtensionById(extensionId);
      // loan-service wraps nportal's raw JSON string in ApiResponse.createSuccessResponse,
      // so the actual Extension lives at response.data.data as a stringified JSON payload
      // that we have to parse before consuming.
      const rawInner = extensionResponse?.data?.data;
      const parsedExtension: Extension | null = typeof rawInner === 'string'
        ? JSON.parse(rawInner)
        : (rawInner ?? null);
      this.currentExtension = parsedExtension;

      this.loadLoanData();
      this.loadExtensionData(parsedExtension);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load extension details',
      });
    } finally {
      this.isLoading = false;
    }
  }

  loadLoanData() {
    if (this.loanStore.loanDetails) {
      this.totalLoanAmount = this.loanStore.loanDetails.amount?.toString() || '';
      this.existingMaturityDate = this.loanStore.loanDetails.extendedMaturityDate || this.loanStore.loanDetails.maturityDate || '';
      this.nextDueDate = this.loanStore.loanDetails.calculatedLoanData?.nextDueDate || '';
    }
  }

  get newMaturityDate(): Date | null {
    if (this.extensionDuration === 'custom') {
      this.disableNewMaturityDate = false;
      return this.customNewMaturityDate;
    }
    this.disableNewMaturityDate = true;
    if (!this.extensionDuration || !this.existingMaturityDate) return null;

    const months = parseInt(this.extensionDuration, 10);
    const newDate = new Date(this.existingMaturityDate);
    newDate.setMonth(newDate.getMonth() + months);
    return newDate;
  }

  loadExtensionData(extension: Extension) {
    if (extension != null) {
      this.notes = extension.notes || '';
      this.requestDate = extension.requestDate || '';
      // Show the existing maturity date persisted on THIS extension. loadLoanData()
      // seeds it from the loan (always the latest/extended maturity), which is wrong
      // for historical extensions, so the selected extension's own value takes priority.
      this.existingMaturityDate = extension.existingMaturityDate || this.existingMaturityDate;
      this.totalExtensionFee = extension.totalExtensionFee?.toString() || '';

      // The persisted newMaturityDate is the source of truth: always load it so a custom
      // date is never lost on reopen.
      this.customNewMaturityDate = extension.newMaturityDate ? new Date(extension.newMaturityDate) : null;

      // Only treat the stored duration as a 3/6 preset when the persisted newMaturityDate
      // actually equals existing + that many months. A custom date frequently rounds (via
      // differenceInMonths, which truncates) to a stored duration of 3 or 6; the old check
      // misread that as a preset and recomputed the date, discarding the days the user
      // entered. Comparing against the computed preset date fixes that collision.
      const durationNum = extension.extensionDuration;
      const isPreset =
        (durationNum === 3 || durationNum === 6) &&
        this.matchesPresetMaturity(durationNum, this.customNewMaturityDate);
      this.extensionDuration = isPreset ? durationNum.toString() : 'custom';
    }
  }

  private matchesPresetMaturity(months: number, storedNewMaturity: Date | null): boolean {
    if (!storedNewMaturity || isNaN(storedNewMaturity.getTime()) || !this.existingMaturityDate) {
      return false;
    }
    const computed = new Date(this.existingMaturityDate);
    if (isNaN(computed.getTime())) return false;
    computed.setMonth(computed.getMonth() + months);
    return format(computed, 'MM/dd/yyyy') === format(storedNewMaturity, 'MM/dd/yyyy');
  }

  setField(field: string, value: any) {
    // Special handling for customNewMaturityDate
    if (field === 'customNewMaturityDate') {
      this.customNewMaturityDate = value;
      return;
    }
    this[field] = value;
  }

  *saveExtensionDetails() {
    this.isLoading = true;
    const newMaturityDateValue = this.newMaturityDate ? format(this.newMaturityDate, 'MM/dd/yyyy') : null;
    // nportal's contract: extensionDuration -1 means "Custom, honor the submitted
    // newMaturityDate". Persisting -1 (instead of a truncated month diff) keeps the
    // stored duration from colliding with the 3/6 presets on later loads and matches
    // what the legacy portal writes for custom extensions.
    const extensionDurationValue =
      this.extensionDuration === 'custom' ? -1 : parseInt(this.extensionDuration, 10);
    try {
      const payload = {
        notes: this.notes,
        requestDate: this.requestDate,
        newMaturityDate: newMaturityDateValue,
        status: this.currentExtension?.status,
        totalExtensionFee: this.totalExtensionFee,
        extensionDuration: extensionDurationValue,
        loanId: this.loanStore.loanDetails.loanId,
        dealName: this.loanStore.loanDetails.dealName,
        existingMaturityDate: this.existingMaturityDate,
        loanExtensionId: this.currentExtension?.loanExtensionId,
      };

      const response = yield this.extensionsService.submitLoanExtension(payload);

      const result = typeof response.data.data === 'string'
        ? JSON.parse(response.data.data)
        : response.data.data;

      if (result.success === false) {
        this.globalStore.notificationStore.showErrorNotification({
          message: result.responseMessage || 'Failed to submit extension request',
        });
        return false;
      }

      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Extension request submitted successfully',
      });

      this.reset();
      return true;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to save extension details',
      });
      return false;
    } finally {
      this.isLoading = false;
    }
  }

  reset() {
    this.extensionDuration = '';
    this.totalExtensionFee = '';
    this.notes = '';
    this.requestDate = '';
    this.existingMaturityDate = '';
    this.nextDueDate = '';
    this.totalLoanAmount = '';
    this.customNewMaturityDate = null;
    this.disableNewMaturityDate = true;
    this.currentExtension = null;
    this.isLoading = false;
  }

  get isReadOnly(): boolean {
    if (!this.globalStore.isInternalPortal) {
      return true;
    }
    const status = this.currentExtension?.status;
    return status === EXTENSION_STATUS.COMPLETED || status === EXTENSION_STATUS.RETRACTED;
  }

  get isValid(): boolean {
    const hasRequiredFields = this.extensionDuration !== '' && this.totalExtensionFee !== '';

    if (!hasRequiredFields) return false;

    const totalLoanAmount = parseFloat(this.totalLoanAmount?.replace(/[^0-9.]/g, '') || '0');
    const currentFee = parseFloat(this.totalExtensionFee?.replace(/[^0-9.]/g, '') || '0');
    const maxAllowed = totalLoanAmount * 0.1;

    return currentFee <= maxAllowed;
  }
}
